import { CopyButton } from '@Components/CopyButton';
import { ReactElement, ReactNode, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { Form } from 'react-bootstrap';
import {
  MdAutoAwesome,
  MdCheckCircle,
  MdChevronLeft,
  MdChevronRight,
  MdEditNote,
  MdExpandLess,
  MdExpandMore,
  MdFileUpload,
  MdInfoOutline,
  MdOpenInNew,
  MdRemoveCircleOutline,
  MdSearch,
  MdTextFields,
  MdWarningAmber
} from 'react-icons/md';
import { Button } from '../../../Components/Button';
import { InteractiveDiv } from '../../../Components/InteractiveEl';
import { SegmentedControlButton } from '../../../Components/SegmentedControlButton';
import { Spinner } from '../../../Components/Spinner';
import { Appearance, Size } from '../../../Components/designConstants';
import { useRevealOnExpand } from '../../../utils/hooks/useRevealOnExpand';
import { CategoryFormulaKey } from './ai-optimize-constants';
import {
  optimizeItemHighlight,
  optimizeTitleForCap,
  resolveCategoryFormulaKey,
  TitleSlots
} from './ai-optimize-utils';
import { BatchTitleOptimizeModal, BatchTitleResult } from './BatchTitleOptimizeModal';
import { PACVUE_MAX_ROWS_PER_FILE } from './pacvue-export-constants';
import { PacvueExportModal } from './PacvueExportModal';
import { AuditedProduct } from './scoring-engine-constants';
import {
  ITEM_HIGHLIGHT_CHAR_LIMIT,
  STATUS_RANK,
  TITLE_CHAR_LIMIT,
  TitleSortMode,
  TitleSortModeType,
  TitleStatus,
  TitleStatusType
} from './title-compliance-constants';
import {
  classifyHighlight,
  classifyTitle,
  findDroppedSpecs,
  findTitleCorrections,
  findTitleRuleViolations,
  formatAiError,
  HighlightClassification,
  matchesTitleQuery,
  overCapTail,
  readTitleRewriteStore,
  summarizeTitleCompliance,
  TitleClassification,
  TitleRewriteStore,
  TitleWordChange,
  writeTitleRewriteStore
} from './title-compliance-utils';

/** Per-row AI title-rewrite state, keyed by product id. */
type RewriteState =
  | { status: 'loading' }
  | {
      status: 'done';
      title: string;
      slots?: TitleSlots;
      reasoning: string;
      generatedAt: number;
      /** Banned characters the strip pass removed — feeds the "corrected" panel. */
      removedChars?: string[];
    }
  | { status: 'error'; message: string };

/** Per-row AI item-highlight state, keyed by product id (parallel to RewriteState). */
type HighlightState =
  | { status: 'loading' }
  | { status: 'done'; value: string; reasoning: string; generatedAt: number }
  | { status: 'error'; message: string };

const ITEMS_PER_PAGE = 12;

const SORT_MODE_OPTIONS = [
  {
    key: TitleSortMode.SUBMITTED,
    label: 'As submitted',
    tooltip: 'Keep the order your ASINs were pasted or uploaded in'
  },
  {
    key: TitleSortMode.OVER_CAP_FIRST,
    label: 'Over cap first',
    tooltip: 'Triage order — longest over-cap titles first, compliant rows last'
  }
] as const;

// One semantic tone per compliance status — keeps each row carrying one clear color.
const STATUS_ICON: Record<TitleStatusType, ReactElement> = {
  [TitleStatus.NEEDS_UPDATE]: <MdWarningAmber className='tone-danger' />,
  [TitleStatus.COMPLIANT]: <MdCheckCircle className='tone-success' />,
  [TitleStatus.MISSING]: <MdRemoveCircleOutline className='tone-neutral' />
};

const STATUS_TONE: Record<TitleStatusType, string> = {
  [TitleStatus.NEEDS_UPDATE]: 'tone-danger',
  [TitleStatus.COMPLIANT]: 'tone-success',
  [TitleStatus.MISSING]: 'tone-neutral'
};

// The Brand→Product→Variant→Size slot formula the v2 prompt builds titles from.
// Rendered in the drawer so reviewers SEE how the title is composed — the piece a
// plain chat rewrite can't show. Order here = order shown.
const SLOT_META: { key: keyof TitleSlots; label: string; modifier: string }[] = [
  { key: 'brand', label: 'Brand', modifier: 'brand' },
  { key: 'product', label: 'Product', modifier: 'product' },
  { key: 'variant', label: 'Variant', modifier: 'variant' },
  { key: 'size', label: 'Size / Count', modifier: 'size' }
];

/**
 * How many `from → to` pairs to list before collapsing the tail into a count. An
 * ALL-CAPS source title re-cased word by word would otherwise print a dozen
 * pairs and bury the one that matters.
 */
const MAX_LISTED_WORD_CHANGES = 3;

const renderWordChanges = (changes: TitleWordChange[]): ReactNode => {
  const shown = changes.slice(0, MAX_LISTED_WORD_CHANGES);
  const rest = changes.length - shown.length;
  return (
    <>
      {shown.map((change, i) => (
        <span key={`${change.from}:${change.to}`}>
          {i > 0 && ', '}
          <span className='title-compliance-word-from'>{change.from}</span>
          {' → '}
          <span className='title-compliance-word-to'>{change.to}</span>
        </span>
      ))}
      {rest > 0 && ` (+${rest} more)`}
    </>
  );
};

/**
 * Everything deterministic we know about a finished rewrite, split by what the
 * reviewer has to DO about it.
 *
 * `warnings` are losses and rule breaks — the reviewer must decide before
 * applying the title. `corrections` are work the tool already did: the fix
 * landed, and saying so was Jen Mohan's actual request against B015OA6C0C
 * ("appreciate it corrected a rogue contribution/capitalization — would love
 * if it could call out"). Both come from pure diffs over the two titles plus
 * the strip pass's own record, so nothing here is a model judgement.
 */
interface TitleNotes {
  warnings: { key: string; body: ReactNode }[];
  corrections: { key: string; body: ReactNode }[];
}

const buildTitleNotes = (
  product: AuditedProduct,
  rw: Extract<RewriteState, { status: 'done' }>,
  hl: HighlightState | undefined
): TitleNotes => {
  const highlightValue = hl?.status === 'done' ? hl.value : undefined;
  const { dropped, recoveredInHighlight } = findDroppedSpecs(
    product.title,
    rw.title,
    highlightValue
  );
  const lost = dropped.filter(d => !recoveredInHighlight.includes(d));
  // A title already within the cap comes back untouched (zero model calls), so
  // there is no rewrite to hold to the tool's own rules. Checking it anyway would
  // report the SCRAPED title's ingredient callouts as something the tool did.
  const wasRewritten = rw.title !== product.title;
  const violations = wasRewritten
    ? findTitleRuleViolations(
        product.title,
        rw.title,
        resolveCategoryFormulaKey(product.category, product.group) === CategoryFormulaKey.CONSUMER
      )
    : { abbreviated: [], ingredientCallouts: [] };
  const corrected = findTitleCorrections(product.title, rw.title, rw.removedChars);

  const warnings: TitleNotes['warnings'] = [];
  const corrections: TitleNotes['corrections'] = [];

  // ~16% of rewrites silently drop a measurement, and it is often the only
  // thing distinguishing this SKU from its siblings.
  if (lost.length > 0) {
    warnings.push({
      key: 'dropped',
      body: (
        <>
          <strong>Dropped from title: {lost.join(', ')}</strong> — if this is what separates this
          SKU from its siblings, add it back before using.
        </>
      )
    });
  }
  if (violations.abbreviated.length > 0) {
    warnings.push({
      key: 'abbreviated',
      body: (
        <>
          <strong>Shortened a word the source spells out:</strong>{' '}
          {renderWordChanges(violations.abbreviated)} — expand it unless the brand uses the short
          form.
        </>
      )
    });
  }
  if (violations.ingredientCallouts.length > 0) {
    warnings.push({
      key: 'ingredients',
      body: (
        <>
          <strong>Ingredient callouts in the title:</strong>{' '}
          {violations.ingredientCallouts.join(', ')} — this category&apos;s formula routes these to
          the item highlight, so move or remove them.
        </>
      )
    });
  }

  // The value still reaches the PDP, just in the other field — informational.
  if (recoveredInHighlight.length > 0) {
    corrections.push({
      key: 'moved',
      body: <>Moved to the item highlight: {recoveredInHighlight.join(', ')}</>
    });
  }
  if (corrected.capitalization.length > 0) {
    corrections.push({
      key: 'capitalization',
      body: <>Capitalization corrected: {renderWordChanges(corrected.capitalization)}</>
    });
  }
  if (corrected.removedChars.length > 0) {
    corrections.push({
      key: 'chars',
      body: (
        <>
          Removed characters Amazon suppresses: <code>{corrected.removedChars.join(' ')}</code>
        </>
      )
    });
  }
  if (corrected.separators) {
    corrections.push({
      key: 'separators',
      body: (
        <>
          Punctuation tidied: {corrected.separators.before} commas/semicolons &rarr;{' '}
          {corrected.separators.after}
        </>
      )
    });
  }

  return { warnings, corrections };
};

const renderTitleNotes = (notes: TitleNotes): ReactNode => {
  if (notes.warnings.length === 0 && notes.corrections.length === 0) {
    return null;
  }
  return (
    <div className='title-compliance-notes'>
      {notes.warnings.length > 0 && (
        <div className='title-compliance-note-group is-warning'>
          <MdWarningAmber />
          <ul>
            {notes.warnings.map(note => (
              <li key={note.key}>{note.body}</li>
            ))}
          </ul>
        </div>
      )}
      {notes.corrections.length > 0 && (
        <div className='title-compliance-note-group is-correction'>
          <MdEditNote />
          <ul>
            {notes.corrections.map(note => (
              <li key={note.key}>{note.body}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

interface TitleComplianceViewProps {
  products: AuditedProduct[];
}

export const TitleComplianceView = ({ products }: TitleComplianceViewProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [batchOpen, setBatchOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [sortMode, setSortMode] = useState<TitleSortModeType>(TitleSortMode.SUBMITTED);

  // Only one row is ever open, so a single ref — attached to whichever wrapper is
  // currently expanded — is enough to reveal the drawer after it renders.
  const expandedRowRef = useRevealOnExpand<HTMLDivElement>(expandedId);

  // Hydrate saved rewrites + highlights (done-state only) from localStorage so a
  // reload doesn't discard work. Loading/error states are inherently ephemeral.
  // Read the store once and derive both initial states from it.
  const [savedRewriteStore] = useState(readTitleRewriteStore);

  const [rewrites, setRewrites] = useState<Record<string, RewriteState>>(() => {
    const initial: Record<string, RewriteState> = {};
    for (const [id, r] of Object.entries(savedRewriteStore)) {
      // Guard on a non-empty title so highlight-only records don't hydrate a bogus
      // empty title rewrite.
      if (r.title && r.title.trim()) {
        initial[id] = {
          status: 'done',
          title: r.title,
          slots: r.slots,
          reasoning: r.reasoning,
          generatedAt: r.generatedAt,
          removedChars: r.removedChars
        };
      }
    }
    return initial;
  });

  // Item-highlight state, hydrated from the same store's optional `highlight` field.
  const [highlights, setHighlights] = useState<Record<string, HighlightState>>(() => {
    const initial: Record<string, HighlightState> = {};
    for (const [id, r] of Object.entries(savedRewriteStore)) {
      if (r.highlight) {
        initial[id] = {
          status: 'done',
          value: r.highlight.value,
          reasoning: r.highlight.reasoning,
          generatedAt: r.highlight.generatedAt
        };
      }
    }
    return initial;
  });

  /**
   * Completed work only (title and/or highlight); loading/error states are
   * dropped so the store stays clean. A record may carry a title rewrite, a
   * highlight, or both.
   *
   * Derived rather than rebuilt per consumer: this same object is what gets
   * persisted AND what the Pacvue export reads, so building it twice would let
   * the file and the saved state drift apart.
   */
  const rewriteStore = useMemo(() => {
    const store: TitleRewriteStore = {};
    for (const [id, state] of Object.entries(rewrites)) {
      if (state.status === 'done') {
        store[id] = {
          title: state.title,
          slots: state.slots,
          reasoning: state.reasoning,
          generatedAt: state.generatedAt,
          removedChars: state.removedChars
        };
      }
    }
    for (const [id, state] of Object.entries(highlights)) {
      if (state.status === 'done') {
        const highlight = {
          value: state.value,
          reasoning: state.reasoning,
          generatedAt: state.generatedAt
        };
        const existing = store[id];
        if (existing) {
          existing.highlight = highlight;
        } else {
          store[id] = { title: '', reasoning: '', generatedAt: state.generatedAt, highlight };
        }
      }
    }
    return store;
  }, [rewrites, highlights]);

  useEffect(() => {
    writeTitleRewriteStore(rewriteStore);
  }, [rewriteStore]);

  /** How many ASINs currently have something worth sending to Pacvue. */
  const exportableCount = useMemo(
    () =>
      Object.values(rewriteStore).filter(r => r.title.trim() || r.highlight?.value.trim()).length,
    [rewriteStore]
  );

  /**
   * One action per row: produce the compliant title AND its item highlight.
   *
   * They were previously two separate clicks, with the highlight buried in the
   * expanded drawer — so testers ran "Optimize", got only a title, and reported
   * the highlight as broken. They are also inherently sequential: the highlight
   * is written to COMPLEMENT the proposed title, so it needs the title first.
   *
   * Title work self-gates — optimizeTitleForCap returns an already-compliant
   * title untouched and makes zero model calls. The highlight always runs,
   * because it is a net-new field every ASIN needs regardless of title length.
   *
   * The two are reported independently on purpose: a failed highlight must not
   * discard a good title, so each writes its own state and the row can end up
   * with one succeeded and one errored.
   */
  const handleOptimize = async (product: AuditedProduct) => {
    setRewrites(prev => ({ ...prev, [product.id]: { status: 'loading' } }));
    setHighlights(prev => ({ ...prev, [product.id]: { status: 'loading' } }));

    // Falls back to the current title if the rewrite fails, so the highlight
    // still has something coherent to complement.
    let proposedTitle = product.title;
    try {
      const result = await optimizeTitleForCap(product);
      proposedTitle = result.title;
      setRewrites(prev => ({
        ...prev,
        [product.id]: {
          status: 'done',
          title: result.title,
          slots: result.slots,
          reasoning: result.reasoning,
          generatedAt: Date.now(),
          removedChars: result.removedChars
        }
      }));
    } catch (err) {
      setRewrites(prev => ({
        ...prev,
        [product.id]: { status: 'error', message: formatAiError(err) }
      }));
    }

    await runHighlight(product, proposedTitle);
  };

  // Shared highlight generation. Assumes the caller has already set the loading
  // state, so the row can show both fields working at once.
  const runHighlight = async (product: AuditedProduct, proposedTitle: string) => {
    try {
      const result = await optimizeItemHighlight(product, proposedTitle);
      setHighlights(prev => ({
        ...prev,
        [product.id]: {
          status: 'done',
          value: result.highlight,
          reasoning: result.reasoning,
          generatedAt: Date.now()
        }
      }));
    } catch (err) {
      setHighlights(prev => ({
        ...prev,
        [product.id]: { status: 'error', message: formatAiError(err) }
      }));
    }
  };

  /**
   * Regenerate ONLY the highlight, from the drawer. No longer the primary way to
   * get one — the row action does that — but kept so you can reroll a highlight
   * against an accepted title without paying for the title pass again.
   */
  const handleGenerateHighlight = async (product: AuditedProduct) => {
    const rw = rewrites[product.id];
    const proposedTitle = rw?.status === 'done' ? rw.title : product.title;
    setHighlights(prev => ({ ...prev, [product.id]: { status: 'loading' } }));
    await runHighlight(product, proposedTitle);
  };

  // Shared by batch results — write completed work into the persisted store.
  // Title and highlight arrive independently so a row that produced only one of
  // the two still saves what it got.
  const applyBatchResult = (productId: string, result: BatchTitleResult) => {
    // Bound to locals so the narrowing survives into the setState closures.
    const { title, highlight } = result;
    if (title) {
      setRewrites(prev => ({
        ...prev,
        [productId]: {
          status: 'done',
          title: title.title,
          slots: title.slots,
          reasoning: title.reasoning,
          generatedAt: Date.now(),
          removedChars: title.removedChars
        }
      }));
    }
    if (highlight) {
      setHighlights(prev => ({
        ...prev,
        [productId]: {
          status: 'done',
          value: highlight.highlight,
          reasoning: highlight.reasoning,
          generatedAt: Date.now()
        }
      }));
    }
  };

  const summary = useMemo(() => summarizeTitleCompliance(products), [products]);

  /**
   * The batch queue: any row with outstanding work, where "work" is a missing
   * title rewrite OR a missing item highlight.
   *
   * It used to be over-cap titles only. That silently excluded every compliant
   * ASIN from the batch — and since the highlight is a net-new field those rows
   * need one too, they would have been left with no highlight and no way to get
   * one except expanding each row by hand. A compliant row still enters the
   * queue for its highlight; optimizeTitleForCap leaves its title alone.
   *
   * Rows with existing work are skipped so a batch never clobbers something the
   * user already reviewed. Missing-title rows are excluded outright — there is
   * nothing to rewrite and nothing to complement.
   */
  const batchCandidates = useMemo(
    () =>
      products.filter(p => {
        if (classifyTitle(p.title).status === TitleStatus.MISSING) {
          return false;
        }
        const needsTitle =
          classifyTitle(p.title).status === TitleStatus.NEEDS_UPDATE &&
          rewrites[p.id]?.status !== 'done';
        const needsHighlight = highlights[p.id]?.status !== 'done';
        return needsTitle || needsHighlight;
      }),
    [products, rewrites, highlights]
  );

  /**
   * Filter, then order by the user's choice.
   *
   * `products` arrives in submission order from both ingest paths, and that is
   * now the default — silently returning a pasted ASIN list in a different order
   * made it impossible to reconcile results against the source sheet. The
   * worst-first triage view is still available, just no longer imposed.
   */
  const processed = useMemo(() => {
    const rows = products
      .filter(p => matchesTitleQuery(p, searchQuery))
      .map(p => ({ product: p, cls: classifyTitle(p.title) }));
    if (sortMode === TitleSortMode.SUBMITTED) {
      return rows;
    }
    // Copy before sorting so the submission-order array is never mutated.
    return [...rows].sort((a, b) => {
      const rank = STATUS_RANK[a.cls.status] - STATUS_RANK[b.cls.status];
      if (rank !== 0) return rank;
      // Within the same bucket, longest-over first.
      return b.cls.length - a.cls.length;
    });
  }, [products, searchQuery, sortMode]);

  const totalPages = Math.max(1, Math.ceil(processed.length / ITEMS_PER_PAGE));
  const page = Math.min(currentPage, totalPages);
  const paginated = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return processed.slice(start, start + ITEMS_PER_PAGE);
  }, [processed, page]);

  const toggleExpand = (id: string) => setExpandedId(prev => (prev === id ? null : id));

  const statTiles = [
    {
      label: 'ASINs Imported',
      value: summary.total,
      tone: 'tone-brand',
      sub: 'Total in this batch'
    },
    {
      label: 'Need Update',
      value: summary.needsUpdate,
      tone: 'tone-danger',
      sub: `Over the ${TITLE_CHAR_LIMIT}-char cap`
    },
    {
      label: 'Already Compliant',
      value: summary.compliant,
      tone: 'tone-success',
      sub: `At or under ${TITLE_CHAR_LIMIT} chars`
    },
    {
      label: 'Missing Title',
      value: summary.missing,
      tone: 'tone-neutral',
      sub: 'No title in scraper row'
    }
  ];

  const lengthTitleAttr = (cls: TitleClassification): string => {
    if (cls.status === TitleStatus.NEEDS_UPDATE) {
      return `${cls.overBy} characters over the ${TITLE_CHAR_LIMIT}-char cap`;
    }
    if (cls.status === TitleStatus.COMPLIANT) return `Within the ${TITLE_CHAR_LIMIT}-char cap`;
    return 'No title to measure';
  };

  // "Redo" only once the row has actually produced something. Keyed on either
  // field, since the action now yields a title and a highlight.
  const actionLabel = (
    rw: RewriteState | undefined,
    hl: HighlightState | undefined,
    loading: boolean
  ): string => {
    if (loading) return 'Working';
    if (rw?.status === 'done' || hl?.status === 'done') return 'Redo';
    return 'Optimize';
  };

  const actionTooltip = (cls: TitleClassification, missing: boolean): { text: string } => {
    if (missing) return { text: 'No title to work from' };
    if (cls.status === TitleStatus.NEEDS_UPDATE) {
      return {
        text: `Generate a compliant ≤${TITLE_CHAR_LIMIT}-char title and a ≤${ITEM_HIGHLIGHT_CHAR_LIMIT}-char item highlight`
      };
    }
    return {
      text: `Title is already within the cap and will be left as-is — generates a ≤${ITEM_HIGHLIGHT_CHAR_LIMIT}-char item highlight`
    };
  };

  const highlightBtnLabel = (hl: HighlightState | undefined): string => {
    if (hl?.status === 'loading') return 'Working';
    if (hl?.status === 'done') return 'Regenerate';
    return 'Generate';
  };

  const renderUpdatedCell = (
    p: AuditedProduct,
    rw: RewriteState | undefined,
    notes: TitleNotes | null
  ): ReactNode => {
    if (rw?.status === 'loading') {
      return (
        <span className='title-compliance-cell-loading'>
          <Spinner size={14} /> Optimizing…
        </span>
      );
    }
    if (rw?.status === 'done') {
      const noteCount = (notes?.warnings.length ?? 0) + (notes?.corrections.length ?? 0);
      return (
        <>
          <span className='title-compliance-cell-updated-text' title={rw.reasoning}>
            {rw.title}
          </span>
          <span className='tc-pill tone-success'>{rw.title.length}</span>
          {/* Nothing below the fold is discoverable unless the row advertises it —
              testers reported generated fields as "missing" for exactly this reason. */}
          {noteCount > 0 && (
            <span
              className={cn('title-compliance-note-chip', {
                'is-warning': (notes?.warnings.length ?? 0) > 0
              })}
              title={
                (notes?.warnings.length ?? 0) > 0
                  ? 'Open the row — this rewrite needs a look before you use it'
                  : 'Open the row — the tool corrected something here'
              }
            >
              {(notes?.warnings.length ?? 0) > 0 ? <MdWarningAmber /> : <MdEditNote />}
              {noteCount}
            </span>
          )}
          <CopyButton stopPropagation tooltipLabel='Copy optimized title' text={rw.title} />
        </>
      );
    }
    if (rw?.status === 'error') {
      return (
        <span className='title-compliance-cell-error' title={rw.message}>
          {rw.message}
        </span>
      );
    }
    return <span className='title-compliance-cell-muted'>Not optimized</span>;
  };

  const renderHighlightCell = (
    p: AuditedProduct,
    hl: HighlightState | undefined,
    hlCls: HighlightClassification | null
  ): ReactNode => {
    if (hl?.status === 'loading') {
      return (
        <span className='title-compliance-cell-loading'>
          <Spinner size={14} /> Generating…
        </span>
      );
    }
    if (hl?.status === 'done') {
      return (
        <>
          <span className='title-compliance-cell-highlight-text' title={hl.reasoning}>
            {hl.value}
          </span>
          <span
            className={cn(
              'tc-pill',
              hlCls?.status === 'compliant' ? 'tone-success' : 'tone-danger'
            )}
          >
            {hlCls?.length}
          </span>
          <CopyButton stopPropagation tooltipLabel='Copy item highlight' text={hl.value} />
        </>
      );
    }
    if (hl?.status === 'error') {
      return (
        <span className='title-compliance-cell-error' title={hl.message}>
          {hl.message}
        </span>
      );
    }
    return <span className='title-compliance-cell-muted'>Not generated</span>;
  };

  const renderDrawerUpdated = (
    rw: RewriteState | undefined,
    notes: TitleNotes | null
  ): ReactNode => {
    if (rw?.status === 'loading') {
      return (
        <p className='title-compliance-cell-loading'>
          <Spinner size={14} /> Optimizing…
        </p>
      );
    }
    if (rw?.status === 'done') {
      return (
        <>
          <p className='title-compliance-updated-box'>{rw.title}</p>
          {notes && renderTitleNotes(notes)}
          {rw.slots && SLOT_META.some(s => rw.slots?.[s.key]?.trim()) && (
            <>
              <p className='title-compliance-slots-label'>Title Structure</p>
              <div className='title-compliance-slots'>
                {SLOT_META.filter(s => rw.slots?.[s.key]?.trim()).map(s => (
                  <span
                    key={s.key}
                    className={`tc-slot-chip tc-slot-chip--${s.modifier}`}
                    title={`${s.label} slot`}
                  >
                    <span>{s.label}</span>
                    <span>{rw.slots?.[s.key]}</span>
                  </span>
                ))}
              </div>
            </>
          )}
          <p className='title-compliance-reasoning'>{rw.reasoning}</p>
        </>
      );
    }
    if (rw?.status === 'error') {
      return <p className='title-compliance-cell-error'>{rw.message}</p>;
    }
    return (
      <p className='title-compliance-placeholder'>
        Click <strong>Optimize</strong> to generate a compliant ≤{TITLE_CHAR_LIMIT}-char title.
      </p>
    );
  };

  const renderDrawerHighlight = (
    rw: RewriteState | undefined,
    hl: HighlightState | undefined
  ): ReactNode => {
    if (hl?.status === 'loading') {
      return (
        <p className='title-compliance-cell-loading'>
          <Spinner size={14} /> Generating…
        </p>
      );
    }
    if (hl?.status === 'done') {
      return (
        <>
          <p className='title-compliance-updated-box'>{hl.value}</p>
          <p className='title-compliance-reasoning'>{hl.reasoning}</p>
        </>
      );
    }
    if (hl?.status === 'error') {
      return <p className='title-compliance-cell-error'>{hl.message}</p>;
    }
    return (
      <p className='title-compliance-placeholder'>
        Click <strong>Generate</strong> to write a ≤{ITEM_HIGHLIGHT_CHAR_LIMIT}-char highlight that
        complements the {rw?.status === 'done' ? 'updated' : 'current'} title.
      </p>
    );
  };

  return (
    <div className='title-compliance-view'>
      {/* Header / context banner */}
      <div className='title-compliance-header'>
        <div className='title-compliance-header-main'>
          <span className='title-compliance-header-icon'>
            <MdTextFields />
          </span>
          <div>
            <h2 className='title-compliance-header-title'>Title Compliance</h2>
            <p className='title-compliance-header-subtitle'>
              Amazon caps product titles at <strong>{TITLE_CHAR_LIMIT} characters</strong> (all
              categories except media) starting <strong>July 27, 2026</strong>.
            </p>
          </div>
        </div>
        <div className='title-compliance-header-actions'>
          <Button
            size={Size.LG}
            leadingElement={<MdAutoAwesome />}
            disabled={batchCandidates.length === 0}
            tooltip={
              batchCandidates.length === 0
                ? { text: 'Every ASIN already has a title and an item highlight' }
                : {
                    text: `Generate titles and item highlights for ${batchCandidates.length} ASINs. Titles already within the cap are left alone.`
                  }
            }
            onClick={() => setBatchOpen(true)}
          >
            Optimize all ({batchCandidates.length.toLocaleString()})
          </Button>
          <Button
            size={Size.LG}
            appearance={Appearance.OUTLINED}
            leadingElement={<MdFileUpload />}
            disabled={exportableCount === 0}
            tooltip={
              exportableCount === 0
                ? { text: 'Generate a title or item highlight first — there is nothing to export' }
                : {
                    text: `Build the Pacvue bulk-update sheet for ${exportableCount} ASINs, split into files of ${PACVUE_MAX_ROWS_PER_FILE.toLocaleString()}.`
                  }
            }
            onClick={() => setExportOpen(true)}
          >
            Export for Pacvue ({exportableCount.toLocaleString()})
          </Button>
        </div>
      </div>

      {/* Stat strip */}
      <div className='title-compliance-stats'>
        {statTiles.map(tile => (
          <div key={tile.label} className='title-compliance-stat'>
            <span className='title-compliance-stat-label'>{tile.label}</span>
            <div>
              <span className={cn('title-compliance-stat-value', tile.tone)}>
                {tile.value.toLocaleString()}
              </span>
              <span className='title-compliance-stat-sub'>{tile.sub}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className='title-compliance-table'>
        <div className='title-compliance-table-controls'>
          <div className='title-compliance-table-controls-left'>
            <span className='title-compliance-count-pill'>
              {processed.length.toLocaleString()} {processed.length === 1 ? 'ASIN' : 'ASINs'}
            </span>
            {/* "As submitted / Over cap first" read as an unlabelled mode switch
                rather than an ordering choice, so the control now says what it
                does and each option carries its own explanation. */}
            <div className='title-compliance-sort'>
              <span className='title-compliance-sort-label'>Sort by</span>
              <SegmentedControlButton
                size={Size.LG}
                selectedOption={sortMode}
                onChange={mode => {
                  setSortMode(mode);
                  setCurrentPage(1);
                }}
                options={SORT_MODE_OPTIONS}
              />
            </div>
          </div>
          <div className='title-compliance-search'>
            <MdSearch />
            <Form.Control
              type='text'
              placeholder='Search ASIN, title, brand…'
              value={searchQuery}
              onChange={e => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>

        <div className='title-compliance-scroll'>
          <div className='title-compliance-grid-header'>
            <span>Current Title / ASIN</span>
            <span className='title-compliance-col-center'>Length</span>
            <span>Updated Title</span>
            <span>Item Highlight</span>
            <span className='title-compliance-col-right'>Action</span>
          </div>

          {paginated.length === 0 ? (
            <div className='product-score-table-empty'>
              <MdInfoOutline />
              <p>No matching ASINs found</p>
              <p>Adjust your search or load a fresh CSV.</p>
            </div>
          ) : (
            paginated.map(({ product: p, cls }) => {
              const isExpanded = expandedId === p.id;
              const rw = rewrites[p.id];
              const hl = highlights[p.id];
              const hlCls = hl?.status === 'done' ? classifyHighlight(hl.value) : null;
              // Computed once per row and shared by the cell chip and the drawer
              // panel, so the deterministic diffs don't run twice.
              const notes = rw?.status === 'done' ? buildTitleNotes(p, rw, hl) : null;
              // Either pass in flight keeps the row's single action busy.
              const isLoading = rw?.status === 'loading' || hl?.status === 'loading';
              const isMissing = cls.status === TitleStatus.MISSING;

              return (
                <div
                  key={p.id}
                  ref={isExpanded ? expandedRowRef : undefined}
                  className={cn('title-compliance-row-wrapper', { 'is-expanded': isExpanded })}
                >
                  <InteractiveDiv
                    className='title-compliance-row'
                    onClick={() => toggleExpand(p.id)}
                  >
                    {/* Current title / ASIN */}
                    <div className='title-compliance-cell-title'>
                      {STATUS_ICON[cls.status]}
                      <div className='title-compliance-cell-title-body'>
                        {/* No `title` attribute — the text now wraps in full, so a
                            tooltip repeating it would be noise. */}
                        <div
                          className={cn('title-compliance-cell-title-text', {
                            'is-missing': isMissing
                          })}
                        >
                          {isMissing ? 'No title provided' : p.title}
                        </div>
                        <div className='title-compliance-cell-meta'>
                          <span className='title-compliance-cell-asin'>{p.asin || '—'}</span>
                          <span>&bull;</span>
                          <span className='title-compliance-cell-brand'>
                            {p.brand || 'Independent'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Length */}
                    <div className='title-compliance-col-center'>
                      <span
                        className={cn('tc-pill', STATUS_TONE[cls.status])}
                        title={lengthTitleAttr(cls)}
                      >
                        {isMissing ? '—' : cls.length}
                      </span>
                    </div>

                    {/* Updated title */}
                    <div className='title-compliance-cell-updated'>
                      {renderUpdatedCell(p, rw, notes)}
                    </div>

                    {/* Item highlight */}
                    <div className='title-compliance-cell-highlight'>
                      {renderHighlightCell(p, hl, hlCls)}
                    </div>

                    {/* Action */}
                    <div className='title-compliance-cell-action'>
                      <Button
                        size={Size.SM}
                        leadingElement={<MdAutoAwesome />}
                        loading={isLoading}
                        disabled={isMissing || isLoading}
                        tooltip={actionTooltip(cls, isMissing)}
                        onClick={e => {
                          e.stopPropagation();
                          handleOptimize(p);
                        }}
                      >
                        {actionLabel(rw, hl, isLoading)}
                      </Button>
                      {isExpanded ? <MdExpandLess /> : <MdExpandMore />}
                    </div>
                  </InteractiveDiv>

                  {/* Expanded drawer — the AI-rewrite context + the "wow" structure view */}
                  {isExpanded && (
                    <div className='title-compliance-drawer'>
                      <div className='title-compliance-drawer-grid'>
                        {/* Current + Updated title */}
                        <div className='title-compliance-card'>
                          <h5 className='title-compliance-card-label'>
                            Current Title ({isMissing ? 0 : cls.length} chars)
                          </h5>
                          {isMissing ? (
                            <p className='title-compliance-placeholder'>
                              No title found in scraper row.
                            </p>
                          ) : (
                            <p className='title-compliance-current-title'>
                              {p.title.slice(0, TITLE_CHAR_LIMIT)}
                              {cls.overBy > 0 && (
                                <span
                                  className='tc-overflow'
                                  title={`${cls.overBy} chars over the ${TITLE_CHAR_LIMIT}-char cap`}
                                >
                                  {overCapTail(p.title)}
                                </span>
                              )}
                            </p>
                          )}
                          {p.asin && (
                            <a
                              className='title-compliance-pdp-link'
                              href={`https://www.amazon.com/dp/${p.asin}`}
                              target='_blank'
                              rel='noreferrer'
                              onClick={e => e.stopPropagation()}
                            >
                              View live PDP <MdOpenInNew />
                            </a>
                          )}

                          <div className='title-compliance-divider'>
                            <div className='title-compliance-card-header'>
                              <h5 className='title-compliance-card-label'>
                                Updated Title{' '}
                                {rw?.status === 'done' && (
                                  <span className='title-compliance-card-ok'>
                                    ({rw.title.length} chars ✓)
                                  </span>
                                )}
                              </h5>
                              {rw?.status === 'done' && (
                                <CopyButton
                                  stopPropagation
                                  tooltipLabel='Copy optimized title'
                                  text={rw.title}
                                />
                              )}
                            </div>
                            {renderDrawerUpdated(rw, notes)}
                          </div>
                        </div>

                        {/* Bullets — AI context */}
                        <div className='title-compliance-card'>
                          <h5 className='title-compliance-card-label'>
                            Bullets — AI Context ({p.featureBullets.length} parsed)
                          </h5>
                          {p.featureBullets.length === 0 ? (
                            <p className='title-compliance-placeholder'>
                              No bullets found in scraper row.
                            </p>
                          ) : (
                            <ul className='title-compliance-bullets'>
                              {p.featureBullets.map((b, i) => (
                                <li key={i}>{b}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>

                      {/* Item Highlight — net-new ≤125-char companion field */}
                      <div className='title-compliance-card'>
                        <div className='title-compliance-card-header'>
                          <h5 className='title-compliance-card-label'>
                            Item Highlight{' '}
                            {hlCls && (
                              <span
                                className={
                                  hlCls.status === 'compliant'
                                    ? 'title-compliance-card-ok'
                                    : 'title-compliance-card-over'
                                }
                              >
                                ({hlCls.length} / {ITEM_HIGHLIGHT_CHAR_LIMIT} chars
                                {hlCls.status === 'compliant' ? ' ✓' : ''})
                              </span>
                            )}
                          </h5>
                          <div className='title-compliance-card-header-actions'>
                            {hl?.status === 'done' && (
                              <CopyButton
                                stopPropagation
                                tooltipLabel='Copy item highlight'
                                text={hl.value}
                              />
                            )}
                            <Button
                              size={Size.SM}
                              appearance={Appearance.OUTLINED}
                              leadingElement={<MdAutoAwesome />}
                              loading={hl?.status === 'loading'}
                              disabled={hl?.status === 'loading'}
                              onClick={e => {
                                e.stopPropagation();
                                handleGenerateHighlight(p);
                              }}
                            >
                              {highlightBtnLabel(hl)}
                            </Button>
                          </div>
                        </div>
                        {renderDrawerHighlight(rw, hl)}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}

          {totalPages > 1 && (
            <div className='title-compliance-pagination'>
              <span>
                Page {page} of {totalPages} ({processed.length.toLocaleString()} ASINs)
              </span>
              <div className='title-compliance-pagination-buttons'>
                <Button
                  size={Size.SM}
                  appearance={Appearance.OUTLINED}
                  leadingElement={<MdChevronLeft />}
                  disabled={page === 1}
                  onClick={() => setCurrentPage(c => Math.max(1, c - 1))}
                >
                  Previous
                </Button>
                <Button
                  size={Size.SM}
                  appearance={Appearance.OUTLINED}
                  trailingElement={<MdChevronRight />}
                  disabled={page === totalPages}
                  onClick={() => setCurrentPage(c => Math.min(totalPages, c + 1))}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {batchOpen && (
        <BatchTitleOptimizeModal
          candidates={batchCandidates}
          onResult={applyBatchResult}
          onClose={() => setBatchOpen(false)}
        />
      )}

      {exportOpen && (
        <PacvueExportModal
          products={products}
          rewriteStore={rewriteStore}
          onClose={() => setExportOpen(false)}
        />
      )}
    </div>
  );
};
